import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Task as TaskIcon,
  Assignment as WorkflowIcon,
  Home as PropertyIcon,
  Settings as SystemIcon,
  Schedule as ReminderIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Archive as ArchiveIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getNotificationCount, getNotifications, markNotificationAsRead, markAllNotificationsAsRead, archiveNotification } from '../services/notificationService';
import socketService from '../services/socketService';
import type { Notification, NotificationFilters } from '../types/notification';

interface NotificationBellProps {
  agencySlug: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ agencySlug }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NotificationFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);
  const [showNewNotification, setShowNewNotification] = useState(false);
  const [newNotification, setNewNotification] = useState<Notification | null>(null);

  const open = Boolean(anchorEl);

  const fetchNotificationCount = async () => {
    try {
      console.log('🔔 NotificationBell: Fetching notification count for agency:', agencySlug);
      const response = await getNotificationCount(agencySlug);
      console.log('🔔 NotificationBell: Received notification count:', response);
      setUnreadCount(response.unreadCount);
    } catch (err: any) {
      console.error('🔔 NotificationBell: Error fetching notification count:', err);
    }
  };

  const fetchNotifications = async (page: number = 1, append: boolean = false) => {
    if (!open) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getNotifications(agencySlug, filters, page, 10);
      
      if (append) {
        setNotifications(prev => [...prev, ...response.notifications]);
      } else {
        setNotifications(response.notifications);
      }
      
      setHasMore(page < response.pagination.pages);
      setCurrentPage(page);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  // Socket.IO connection and event handling
  useEffect(() => {
    if (!user || !token) return;

    let unsubscribeNotification: (() => void) | null = null;
    let unsubscribeCount: (() => void) | null = null;
    let unsubscribeAuth: (() => void) | null = null;

    const connectSocket = async () => {
      try {
        await socketService.connect(token, agencySlug);
        setSocketConnected(true);
        console.log('✅ Socket.IO connected for notifications');
      } catch (error) {
        console.error('❌ Failed to connect Socket.IO:', error);
        setSocketConnected(false);
      }
    };

    // Set up event listeners
    unsubscribeNotification = socketService.onNotification((notification) => {
      console.log('🔔 NotificationBell: New real-time notification received:', notification);
      console.log('🔔 NotificationBell: Current unread count before update:', unreadCount);
      console.log('🔔 NotificationBell: Notification structure:', JSON.stringify(notification, null, 2));
      
      // Add new notification to the top of the list
      setNotifications(prev => {
        console.log('🔔 NotificationBell: Previous notifications count:', prev.length);
        const newNotifications = [notification, ...prev];
        console.log('🔔 NotificationBell: New notifications count:', newNotifications.length);
        return newNotifications;
      });
      
      // Update unread count
      setUnreadCount(prev => {
        const newCount = prev + 1;
        console.log('🔔 NotificationBell: Updated unread count from', prev, 'to', newCount);
        return newCount;
      });
      
      // Show notification toast
      setNewNotification(notification);
      setShowNewNotification(true);
      console.log('🔔 NotificationBell: Toast notification triggered');
    });

    unsubscribeCount = socketService.onNotificationCount((count) => {
      console.log('📊 Notification count updated:', count);
      setUnreadCount(count);
    });

    unsubscribeAuth = socketService.onAuth((success, message) => {
      setSocketConnected(success);
      if (!success) {
        console.error('❌ Socket.IO authentication failed:', message);
      }
    });

    // Connect to Socket.IO
    connectSocket();

    // Cleanup on unmount
    return () => {
      if (unsubscribeNotification) unsubscribeNotification();
      if (unsubscribeCount) unsubscribeCount();
      if (unsubscribeAuth) unsubscribeAuth();
      socketService.disconnect();
    };
  }, [user, token, agencySlug]);

  useEffect(() => {
    // Always fetch count initially, then rely on real-time updates
    fetchNotificationCount();
  }, [agencySlug]);

  // Debug effect to log unread count changes
  useEffect(() => {
    console.log('🔔 NotificationBell: Unread count changed to:', unreadCount);
  }, [unreadCount]);

  useEffect(() => {
    if (open) {
      fetchNotifications(1, false);
    }
  }, [open, filters]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setNotifications([]);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(agencySlug, notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, isRead: true, readAt: new Date().toISOString() }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(agencySlug);
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      setUnreadCount(0);
    } catch (err: any) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleArchive = async (notificationId: string) => {
    try {
      await archiveNotification(agencySlug, notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
    } catch (err: any) {
      console.error('Error archiving notification:', err);
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchNotifications(currentPage + 1, true);
    }
  };

  const handleCloseSnackbar = () => {
    setShowNewNotification(false);
    setNewNotification(null);
  };

  const getNotificationIcon = (category: string) => {
    switch (category) {
      case 'task':
        return <TaskIcon />;
      case 'workflow':
        return <WorkflowIcon />;
      case 'property':
        return <PropertyIcon />;
      case 'system':
        return <SystemIcon />;
      case 'reminder':
        return <ReminderIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ ml: 1 }}
        aria-label="Notifications"
      >
        <Badge 
          badgeContent={unreadCount} 
          color="error"
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: unreadCount > 0 ? '#f44336' : 'transparent',
              color: unreadCount > 0 ? 'white' : 'transparent'
            }
          }}
        >
          <NotificationsIcon sx={{ color: unreadCount > 0 ? 'primary.main' : 'action.active' }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Notifications</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Socket connection indicator */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: socketConnected ? 'success.main' : 'error.main',
                  mr: 1
                }}
                title={socketConnected ? 'Real-time connected' : 'Real-time disconnected'}
              />
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllAsRead} sx={{ mr: 1 }}>
                  Mark all read
                </Button>
              )}
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Filters */}
          <Box sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category || ''}
                label="Category"
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value as any || undefined }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="task">Task</MenuItem>
                <MenuItem value="workflow">Workflow</MenuItem>
                <MenuItem value="property">Property</MenuItem>
                <MenuItem value="system">System</MenuItem>
                <MenuItem value="reminder">Reminder</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.isRead === undefined ? '' : filters.isRead.toString()}
                label="Status"
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  isRead: e.target.value === '' ? undefined : e.target.value === 'true' 
                }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="false">Unread</MenuItem>
                <MenuItem value="true">Read</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && notifications.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography color="text.secondary">
                No notifications found
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ p: 0 }}>
                {notifications.map((notification) => {
                  const propertyId = notification.relatedEntity?.propertyId?._id || notification.relatedEntity?.propertyId;
                  const handleNotificationClick = async () => {
                    // Mark as read if not already read
                    if (!notification.isRead) {
                      await handleMarkAsRead(notification._id);
                    }
                    // Navigate to property page if propertyId exists
                    if (propertyId && agencySlug) {
                      handleClose(); // Close the notification popover
                      // If this is a consultation report notification, navigate to edit page
                      const isConsultationReport = notification.message?.toLowerCase().includes('marketing appraisal') || 
                                                   notification.message?.toLowerCase().includes('consultation report') ||
                                                   notification.title?.toLowerCase().includes('marketing appraisal') ||
                                                   notification.title?.toLowerCase().includes('consultation report');
                      if (isConsultationReport) {
                        navigate(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/edit`);
                      } else {
                        navigate(`/agencies/${agencySlug}/properties/${propertyId}`);
                      }
                    }
                  };
                  
                  return (
                  <ListItem
                    key={notification._id}
                    onClick={propertyId ? handleNotificationClick : undefined}
                    sx={{
                      backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                      borderLeft: notification.isRead ? 'none' : `4px solid ${notification.priority === 'urgent' ? 'error.main' : 'primary.main'}`,
                      cursor: propertyId ? 'pointer' : 'default',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getNotificationIcon(notification.category)}
                    </ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" component="span" sx={{ fontWeight: notification.isRead ? 'normal' : 'bold' }}>
                            {notification.title}
                          </Typography>
                          <Chip
                            label={notification.priority}
                            size="small"
                            color={getPriorityColor(notification.priority) as any}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'block', mt: 0.5 }}>
                          <Typography variant="body2" component="span" display="block" color="text.secondary">
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" component="span" display="block" color="text.secondary">
                            {formatTime(notification.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {!notification.isRead && (
                        <IconButton
                          size="small"
                          onClick={() => handleMarkAsRead(notification._id)}
                          title="Mark as read"
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleArchive(notification._id)}
                        title="Archive"
                      >
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                  );
                })}
              </List>

              {hasMore && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    onClick={handleLoadMore}
                    disabled={loading}
                    variant="outlined"
                    size="small"
                  >
                    {loading ? <CircularProgress size={16} /> : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Popover>

      {/* New notification toast */}
      <Snackbar
        open={showNewNotification}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {newNotification?.title}
          </Typography>
          <Typography variant="body2">
            {newNotification?.message}
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationBell; 